import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import { MdEdit, MdSave, MdCancel } from "react-icons/md";
import log_activity from "../activity/logger";
import { useAuth } from "../../Context/AuthContext";
import styles from "./detail.module.scss";

export const RoleAdjustments = ({ playerID, playerName }) => {
  const { userInfo } = useAuth();
  const [allRoles, setAllRoles] = useState([]);
  const [roleAdjustments, setRoleAdjustments] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editForm, setEditForm] = useState({ role: "", description: "", count: 0 });
  const [playerRoleCounts, setPlayerRoleCounts] = useState({});

  // Fetch all roles, player's role adjustments, and player's actual role counts on component mount
  useEffect(() => {
    if (playerID) {
      fetchAllRoles();
      fetchRoleAdjustments();
      fetchPlayerRoleCounts();
    }
  }, [playerID]);

  const fetchAllRoles = async () => {
    setLoading(true);
    try {
      const snapshot = await db
        .collection("roles-dropdown")
        .orderBy("name")
        .get();
      
      const roles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAllRoles(roles);
    } catch (error) {
      console.error("Error fetching all roles:", error);
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleAdjustments = async () => {
    try {
      const snapshot = await db
        .collection("role-adjustments")
        .where("playerID", "==", playerID)
        .get();
      
      const adjustments = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        adjustments[data.role] = {
          id: doc.id,
          count: data.count || 0,
          description: data.description || ""
        };
      });
      
      setRoleAdjustments(adjustments);
    } catch (error) {
      console.error("Error fetching role adjustments:", error);
    }
  };

  const fetchPlayerRoleCounts = async () => {
    try {
      const response = await db
        .collection("reports")
        .where("playerProfile", "==", playerID)
        .get();
      
      const counts = {};
      response.docs.forEach(report => {
        if (report.data().typeSpecificFields?.roles && Array.isArray(report.data().typeSpecificFields.roles)) {
          report.data().typeSpecificFields.roles.forEach(role => {
            if (role && role.trim() !== '') {
              counts[role] = (counts[role] || 0) + 1;
            }
          });
        }
      });
      
      setPlayerRoleCounts(counts);
    } catch (error) {
      console.error("Error fetching player role counts:", error);
    }
  };

  const handleSaveAdjustment = async () => {
    if (!selectedRole) return;

    try {
      const adjustment = roleAdjustments[selectedRole.name];
      const adjustmentCount = editForm.count;

      if (adjustment && adjustment.id) {
        // Update existing adjustment
        await db.collection("role-adjustments").doc(adjustment.id).update({
          count: adjustmentCount,
          description: selectedRole.explanation || "",
          updatedAt: new Date(),
          updatedBy: userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : "Unknown User"
        });
        
        toast.success(`Role adjustment for "${selectedRole.name}" updated successfully`);
      } else {
        // Create new adjustment
        const newAdjustment = {
          playerID,
          playerName,
          role: selectedRole.name,
          description: selectedRole.explanation || "",
          count: adjustmentCount,
          createdAt: new Date(),
          createdBy: userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : "Unknown User"
        };

        await db.collection("role-adjustments").add(newAdjustment);
        toast.success(`Role adjustment for "${selectedRole.name}" added successfully`);
      }
      
      // Refresh data
      await fetchRoleAdjustments();
      
      // Close modal and reset form
      setShowAdjustmentModal(false);
      setSelectedRole(null);
      setEditForm({ role: "", description: "", count: 0 });
      
      // Log activity (handle separately to avoid affecting the main save operation)
      try {
        log_activity(
          `${userInfo ? userInfo.firstName + " " + userInfo.lastName : "Unknown User"}`,
          `${adjustment ? 'Updated' : 'Added'} role adjustment: ${selectedRole.name} (${adjustmentCount > 0 ? '+' : ''}${adjustmentCount}) for ${playerName}`
        );
      } catch (logError) {
        console.error("Error logging activity:", logError);
        // Don't show error toast for logging failures
      }
    } catch (error) {
      console.error("Error saving role adjustment:", error);
      toast.error("Failed to save role adjustment");
    }
  };

  const openAdjustmentModal = (role) => {
    setSelectedRole(role);
    const adjustment = roleAdjustments[role.name];
    setEditForm({
      role: role.name,
      description: role.explanation || "",
      count: adjustment ? adjustment.count : 0
    });
    setShowAdjustmentModal(true);
  };

  const closeAdjustmentModal = () => {
    setShowAdjustmentModal(false);
    setSelectedRole(null);
    setEditForm({ role: "", description: "", count: 0 });
  };

  const getLiveCount = (roleName) => {
    return playerRoleCounts[roleName] || 0;
  };

  const getAdjustmentCount = (roleName) => {
    const adjustment = roleAdjustments[roleName];
    return adjustment ? adjustment.count : 0;
  };

  const getFinalCount = (roleName) => {
    return getLiveCount(roleName) + getAdjustmentCount(roleName);
  };

  if (loading) {
    return <div className={styles.loading}>Loading roles...</div>;
  }

  return (
    <div className={styles.roleAdjustmentsContainer}>
      {/* Description Text */}
      <div className={styles.descriptionText}>
        <h3>Roles and Adjustments</h3>
        <p>Roles (attributes, traits, etc.) we expect the player to fulfill at the projected level. This section is used to add and remove role values. Live Count is the roles counted from the reports, the adjustment is the value we are manually adding or subtracting and final count is what is used to display on the player profile.</p>
      </div>

      {/* Roles Table */}
      <div className={styles.rolesTable}>
        <table>
          <thead>
            <tr>
              <th>Role</th>
              <th>Description</th>
              <th>Live Count</th>
              <th>Adjustment</th>
              <th>Final Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {allRoles.map((role) => (
              <tr key={role.id}>
                <td className={styles.roleName}>{role.name}</td>
                <td className={styles.roleDescription}>
                  {role.explanation || "No description available"}
                </td>
                <td className={styles.liveCount}>{getLiveCount(role.name)}</td>
                <td className={styles.adjustmentCount}>
                  <span className={getAdjustmentCount(role.name) >= 0 ? styles.positive : styles.negative}>
                    {getAdjustmentCount(role.name) > 0 ? '+' : ''}{getAdjustmentCount(role.name)}
                  </span>
                </td>
                <td className={styles.finalCount}>{getFinalCount(role.name)}</td>
                <td className={styles.actions}>
                  <button
                    className={styles.adjustButton}
                    onClick={() => openAdjustmentModal(role)}
                  >
                    <MdEdit />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Adjustment Modal */}
      {showAdjustmentModal && selectedRole && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>Adjust Role: {selectedRole.name}</h3>
              <button className={styles.closeButton} onClick={closeAdjustmentModal}>
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.roleInfo}>
                <div className={styles.infoRow}>
                  <label>Role:</label>
                  <span>{selectedRole.name}</span>
                </div>
                <div className={styles.infoRow}>
                  <label>Description:</label>
                  <span>{selectedRole.explanation || "No description available"}</span>
                </div>
                <div className={styles.infoRow}>
                  <label>Live Count:</label>
                  <span>{getLiveCount(selectedRole.name)}</span>
                </div>
                <div className={styles.infoRow}>
                  <label>Current Adjustment:</label>
                  <span className={getAdjustmentCount(selectedRole.name) >= 0 ? styles.positive : styles.negative}>
                    {getAdjustmentCount(selectedRole.name) > 0 ? '+' : ''}{getAdjustmentCount(selectedRole.name)}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <label>Final Count:</label>
                  <span className={styles.finalCount}>{getFinalCount(selectedRole.name)}</span>
                </div>
              </div>
              
              <div className={styles.adjustmentForm}>
                <div className={styles.formGroup}>
                  <label>Adjustment Value:</label>
                  <input
                    type="number"
                    value={editForm.count}
                    onChange={(e) => setEditForm({ ...editForm, count: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    min="-100"
                    max="100"
                  />
                  <small>Use positive numbers to add, negative to subtract</small>
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button className={styles.saveButton} onClick={handleSaveAdjustment}>
                <MdSave /> Save
              </button>
              <button className={styles.cancelButton} onClick={closeAdjustmentModal}>
                <MdCancel /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {allRoles.length === 0 && (
        <div className={styles.noRoles}>
          <p>No roles found in the system.</p>
        </div>
      )}
    </div>
  );
}; 